import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Eye, FileText, Loader2, ArrowLeft, ShieldAlert, Download } from "lucide-react";
import toast from "react-hot-toast";
import axios from "../../api/axios";

import {
  masterPanelCard,
  masterHeadingMain,
  masterTableWrap,
  masterTable,
  masterTableHead,
  masterTableRow,
  masterPageContainer,
  masterHeaderContent,
  masterHeaderBrand,
  masterHeaderIconBox,
  masterTableContainer,
  masterThCode,
  masterThName,
  masterTdCode,
  masterTdName,
  masterTdEmpty,
} from "../../GlobalComponents/MasterCssClass";

const StudyMaterialView = () => {
  const { catId } = useParams();
  const [searchParams] = useSearchParams();
  const examCodeFromUrl = searchParams.get("examCode");

  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);

  // Secure PDF View States
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [isRestricted, setIsRestricted] = useState(true);
  const [activeRecordId, setActiveRecordId] = useState(null);
  const [selectedPdfTitle, setSelectedPdfTitle] = useState("");
  const [selectedExamName, setSelectedExamName] = useState("");
  const [rawBase64Pdf, setRawBase64Pdf] = useState("");
  const [selectedPdfFileName, setSelectedPdfFileName] = useState("StudyMaterial.pdf");
  const [isBlackedOut, setIsBlackedOut] = useState(false);
  const [pdfPages, setPdfPages] = useState([]);
  const [renderingPdf, setRenderingPdf] = useState(false);

  const lastToastTimeRef = useRef(0);
  const showThrottledToast = (message) => {
    const now = Date.now();
    if (now - lastToastTimeRef.current > 3000) {
      lastToastTimeRef.current = now;
      toast.error(message);
    }
  };

  const fetchMaterials = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`/master/study-material-view/all?examCode=${examCodeFromUrl || ""}&catId=${catId || ""}`);
      setMaterials(res.data?.data || []);
    } catch {
      toast.error("FAILED TO LOAD STUDY MATERIALS");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, [catId, examCodeFromUrl]);

  const portalExamHeaderName = materials.length > 0 && materials[0]?.examName 
    ? materials[0].examName 
    : examCodeFromUrl || "";

  const getTopicPath = (item) =>
    [item.topicName, item.subTopicName, item.nestedTopicName, item.nestedSubTopicName]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" / ") || "---";

  // Anti-Cheating & Security Listeners
  useEffect(() => {
    if (!selectedPdfUrl || !isRestricted) return;

    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      return false;
    };

    const handleKeyDown = (e) => {
      if (
        e.key === "PrintScreen" || 
        e.code === "PrintScreen" ||
        e.keyCode === 44 ||
        e.ctrlKey || 
        e.metaKey || 
        e.altKey ||
        e.key === "F12"
      ) {
        e.preventDefault();
        e.stopPropagation();
        setIsBlackedOut(true);
        showThrottledToast("Security Restriction: Screenshot/Capture attempt blocked.");
        return false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden || document.visibilityState === "hidden") {
        setIsBlackedOut(true);
      }
    };

    const handleWindowBlur = () => setIsBlackedOut(true);
    const handlePageHide = () => setIsBlackedOut(true);

    window.addEventListener("contextmenu", handleContextMenu, { capture: true });
    window.addEventListener("keydown", handleKeyDown, { capture: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.removeEventListener("contextmenu", handleContextMenu, { capture: true });
      window.removeEventListener("keydown", handleKeyDown, { capture: true });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [selectedPdfUrl, isRestricted]);

  // Render PDF pages cleanly via React state
  useEffect(() => {
    if (!rawBase64Pdf) return;

    let isMounted = true;
    const loadAndRenderPdf = async () => {
      setRenderingPdf(true);
      try {
        if (!window.pdfjsLib) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js";
            script.onload = () => {
              window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js";
              resolve();
            };
            script.onerror = reject;
            document.head.appendChild(script);
          });
        }

        const binaryString = atob(rawBase64Pdf);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const loadingTask = window.pdfjsLib.getDocument({ data: bytes });
        const pdfDoc = await loadingTask.promise;
        const pageImages = [];

        for (let pageNum = 1; pageNum <= pdfDoc.numPages; pageNum++) {
          const page = await pdfDoc.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1.5 });
          
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport: viewport,
          }).promise;

          pageImages.push(canvas.toDataURL());
        }

        if (isMounted) {
          setPdfPages(pageImages);
        }
      } catch (err) {
        console.error("PDF render error:", err);
      } finally {
        if (isMounted) setRenderingPdf(false);
      }
    };

    loadAndRenderPdf();

    return () => {
      isMounted = false;
    };
  }, [rawBase64Pdf]);

  const handleViewPdf = async (recordId, title, examName) => {
    setActiveRecordId(recordId);
    try {
      const res = await axios.get(`/master/study-material/${recordId}`);
      const docItem = res.data?.data;
      if (docItem && docItem.documentPaths && docItem.documentPaths.length > 0) {
        const base64Str = docItem.documentPaths[0];
        const fileName = docItem.documentNames?.[0] || "StudyMaterial.pdf";
        setRawBase64Pdf(base64Str);
        setSelectedPdfFileName(fileName);
        setSelectedPdfUrl("active");

        setIsRestricted(docItem.restrictionMode ?? true);
        setSelectedPdfTitle(title || "Study Material PDF");
        setSelectedExamName(examName || docItem.examName || "");
        setIsBlackedOut(false);
      } else {
        toast.error("PDF NOT FOUND");
      }
    } catch {
      toast.error("FAILED TO OPEN PDF");
    } finally {
      setActiveRecordId(null);
    }
  };

  const handleDownloadPdf = () => {
    try {
      const byteCharacters = atob(rawBase64Pdf);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const blob = new Blob([new Uint8Array(byteNumbers)], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = selectedPdfFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("PDF Downloaded successfully");
    } catch {
      toast.error("Failed to download PDF");
    }
  };

  const handleBackToList = () => {
    setSelectedPdfUrl(null);
    setSelectedPdfTitle("");
    setSelectedExamName("");
    setRawBase64Pdf("");
    setPdfPages([]);
    setIsBlackedOut(false);
  };

  if (selectedPdfUrl) {
    return (
      <div className={`${masterPageContainer} h-full flex flex-col overflow-hidden p-1 ${isRestricted ? "select-none" : ""}`} style={{ WebkitUserSelect: isRestricted ? "none" : "auto" }}>
        {/* Top Header */}
        <div className={`${masterPanelCard} p-2 mb-1 flex flex-col md:flex-row items-start md:items-center justify-between gap-2`}>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <button
              type="button"
              onClick={handleBackToList}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0E3652] hover:bg-[#164f77] text-white text-xs font-bold rounded-lg shadow-sm transition-all shrink-0"
            >
              <ArrowLeft size={14} /> Back
            </button>
            <div className={masterHeaderBrand}>
              <div className="flex flex-col">
                {selectedExamName && (
                  <span className="text-[11px] uppercase font-extrabold text-blue-600 tracking-wider">
                    Exam: <span className="text-indigo-700">{selectedExamName}</span>
                  </span>
                )}
                <h1 className={`${masterHeadingMain} truncate max-w-[200px] sm:max-w-md`} title={selectedPdfTitle}>
                  {selectedPdfTitle}
                </h1>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto justify-end">
            {!isRestricted && (
              <button
                type="button"
                onClick={handleDownloadPdf}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg shadow-sm transition-all"
              >
                <Download size={14} /> Download PDF
              </button>
            )}

            {isRestricted ? (
              <div className="flex items-center gap-1 text-amber-600 text-xs font-semibold bg-amber-50 px-2 py-1 rounded border border-amber-200">
                <ShieldAlert size={14} /> Security Mode Active
              </div>
            ) : (
              <div className="flex items-center gap-1 text-emerald-600 text-xs font-semibold bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
                Standard View Mode
              </div>
            )}
          </div>
        </div>

        {/* Secure Scrollable Container */}
        <div 
          className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-2 relative bg-slate-950 rounded-lg overflow-y-auto`}
          style={{ height: "calc(100vh - 120px)", WebkitOverflowScrolling: "touch" }}
        >
          <div className="w-full flex flex-col items-center py-2">
            {renderingPdf && (
              <div className="text-white text-sm font-semibold flex flex-col items-center gap-3 mt-24">
                <Loader2 className="animate-spin text-blue-400" size={32} />
                <p>Please wait, loading PDF pages...</p>
              </div>
            )}
            {!renderingPdf && pdfPages.map((pageSrc, index) => (
              <img 
                key={index} 
                src={pageSrc} 
                alt={`Page ${index + 1}`} 
                className="mb-4 shadow-lg mx-auto rounded max-w-full select-none"
                style={{ pointerEvents: isRestricted ? "none" : "auto" }}
              />
            ))}
          </div>

          {/* Solid Pitch-Black Privacy Shield Overlay */}
          {isRestricted && isBlackedOut && (
            <div className="absolute inset-0 bg-black flex flex-col items-center justify-center text-white z-50 p-4 text-center">
              <ShieldAlert size={52} className="text-amber-500 mb-3 animate-pulse" />
              <p className="text-sm font-bold tracking-wide px-4">Protected Document: Screen blackened due to security restriction or screenshot attempt.</p>
              <button
                type="button"
                onClick={() => setIsBlackedOut(false)}
                className="mt-4 px-4 py-1.5 bg-[#0E3652] hover:bg-[#164f77] text-white text-xs font-bold rounded-lg shadow transition-all"
              >
                Resume Viewing
              </button>
            </div>
          )}

          <div 
            className="absolute inset-0 pointer-events-none" 
            onContextMenu={(e) => {
              if (isRestricted) e.preventDefault();
            }}
          />
        </div>
      </div>
    );
  }

  // Default Table View
  return (
    <div className={`${masterPageContainer} h-full flex flex-col overflow-y-auto custom-scrollbar p-1 select-none`} style={{ WebkitUserSelect: "none" }}>
      <div className={`${masterPanelCard} p-2 mb-1`}>
        <div className={masterHeaderContent}>
          <div className={masterHeaderBrand}>
            <div className={masterHeaderIconBox}>
              <FileText size={13} />
            </div>
            <h1 className={masterHeadingMain}>
              Study Materials Portal{" "}
              {portalExamHeaderName && (
                <span className="text-indigo-600 font-extrabold">
                  - {portalExamHeaderName}
                </span>
              )}
            </h1>
          </div>
        </div>
      </div>

      <div className={`${masterTableContainer} flex-1 min-h-0 flex flex-col p-1`}>
        <div className={`${masterTableWrap} overflow-x-auto overflow-y-auto custom-scrollbar flex-1 max-h-[calc(100vh-200px)]`}>
          <table className={masterTable}>
            <thead>
              <tr className={masterTableHead}>
                <th className={masterThCode}>SL NO</th>
                <th className={masterThName}>EXAM NAME</th>
                <th className={masterThName}>SUBJECT</th>
                <th className={masterThName}>TOPIC PATH</th>
                <th className={masterThName}>TITLE</th>
                <th className={masterThName}>PDF NAME</th>
                <th className={masterThName}>VIEW PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className={masterTdEmpty}>
                    <Loader2 size={16} className="animate-spin inline mr-1" /> Loading Materials...
                  </td>
                </tr>
              ) : materials.length > 0 ? (
                materials.map((item, index) => (
                  <tr key={item._id} className={masterTableRow}>
                    <td className={masterTdCode}>{index + 1}</td>
                    <td className={`${masterTdName} font-semibold text-indigo-700`}>{item.examName}</td>
                    <td className={masterTdName}>{item.subjectName}</td>
                    <td className={masterTdName}>{getTopicPath(item)}</td>
                    <td className={masterTdName}>{item.title}</td>
                    <td className={masterTdName}>{item.documentNames?.[0] || "---"}</td>
                    <td className={masterTdName}>
                      <button
                        type="button"
                        onClick={() => handleViewPdf(item._id, item.title, item.examName)}
                        disabled={activeRecordId === item._id}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#0E3652] hover:bg-[#164f77] text-white text-xs font-bold rounded-lg shadow-sm transition-all"
                        title="View PDF"
                      >
                        {activeRecordId === item._id ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} View
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className={masterTdEmpty}>
                    No Study Materials Found for this Exam & Category
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StudyMaterialView;
